// Global
import {
  RichTextField,
  Text,
  TextField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef } from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';
import { DartComponents } from 'lib/templates/Feature.Dart.model';

// Local
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import { tailwindVariants } from './HeadlineTailwind';

export type HeadlineProps = ComponentProps & DartComponents.Headline.Headline;

type Alignment = 'Center' | 'Left' | undefined;

type BackgroundColor = 'BrandColor' | 'Default' | 'Light' | undefined;

const Headline = (props: HeadlineProps): JSX.Element => {
  const { backgroundColor = 'Default', alignContent = 'Center' } = props?.params || {};

  const { base, contentWrapper, headlineText, description, textAlignment, componentBG } =
    tailwindVariants({
      background: backgroundColor as BackgroundColor,
      alignment: alignContent as Alignment,
    });

  const { sitecoreContext } = useSitecoreContext();

  const id = props?.params?.RenderingIdentifier;

  /* This code snippet is added for an event listener to the `containerRef` element for handling click events, when clicked scroll to specific ID */
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLAnchorElement;

      // Only handle anchor links with hashes
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const href = target.getAttribute('href')!;
        const targetId = href.slice(1);
        const elementToScroll = document.getElementById(targetId);

        if (elementToScroll) {
          e.preventDefault(); // prevent default jump
          elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [containerRef]);

  return (
    <div
      className={componentBG()}
      data-component="authorable/headline"
      id={id ? id : undefined}
      tabIndex={id ? -1 : 1}
    >
      <Container>
        <div ref={containerRef} className={base()}>
          <div className={(contentWrapper(), textAlignment())}>
            {props?.fields ? (
              <>
                {props?.fields?.headlineTitle?.value && (
                  <Text
                    className={headlineText()}
                    encode={false}
                    field={props?.fields?.headlineTitle as TextField}
                    tag="h2"
                  />
                )}
                {props?.fields?.headlineDescription?.value && (
                  <RichTextA11yWrapper
                    className={description()}
                    field={props?.fields?.headlineDescription as RichTextField}
                  />
                )}
              </>
            ) : null}
            {!props?.fields && sitecoreContext?.route?.fields ? (
              <>
                <Text
                  className={headlineText()}
                  encode={false}
                  field={sitecoreContext?.route?.fields?.headlineTitle as TextField}
                  tag="h1"
                />
                <RichTextA11yWrapper
                  className={description()}
                  field={sitecoreContext?.route?.fields?.headlineDescription as RichTextField}
                />
              </>
            ) : null}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Headline;
